'use client';
import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { Slot } from "@radix-ui/react-slot";
import { Icon } from "@tameeni/icons";
import * as React from "react";
import { Button } from "../button/button.js";
import fileUploaderVariants from "./file-uploader-variants.js";
const FileUploader = React.forwardRef(
  ({
    className,
    id,
    files: controlledFiles,
    onFilesChange,
    multiple = false,
    maxFiles = 5,
    maxFileCountErrorMessage,
    maxFileSize = 10 * 1024 * 1024,
    // 10MB default
    maxFileSizeErrorMessage,
    acceptedFileTypes,
    acceptedFileTypesErrorMessage,
    uploadIcon,
    isInvalid,
    invalidMessage,
    succeeded,
    isLoading,
    placeholder = "Choose files or drag and drop here",
    showPreview = true,
    description = "You can upload multiple files (Total Size: 14MB)",
    ...props
  }, ref) => {
    const [internalFiles, setInternalFiles] = React.useState(controlledFiles || []);
    const [error, setError] = React.useState("");
    const files = controlledFiles !== void 0 ? controlledFiles : internalFiles;
    React.useEffect(() => {
      if (controlledFiles !== void 0) setInternalFiles(controlledFiles);
    }, [controlledFiles]);
    const setFiles = React.useCallback(
      (newFiles) => {
        if (controlledFiles !== void 0) {
          onFilesChange == null ? void 0 : onFilesChange(newFiles);
        } else {
          setInternalFiles(newFiles);
          onFilesChange == null ? void 0 : onFilesChange(newFiles);
        }
      },
      [controlledFiles, onFilesChange]
    );
    const {
      root,
      input,
      label,
      uploadIcon: uploadIconSlot,
      fileList,
      fileItem,
      description: descriptionSlot
    } = fileUploaderVariants({
      error: isInvalid,
      success: succeeded,
      dragActive: false,
      hasFiles: !multiple && files.length > 0,
      loading: isLoading
    });
    const handleFileSelect = React.useCallback(
      (fileList2) => {
        if (!fileList2) return;
        const fileArray = Array.from(fileList2);
        const errors = [];
        const newFiles = multiple ? [...files] : [];
        if (multiple && fileArray.length + files.length > maxFiles) {
          errors.push(
            maxFileCountErrorMessage || `Maximum ${maxFiles} files allowed`
          );
        }
        fileArray.forEach((file) => {
          var _a;
          if (file.size > maxFileSize) {
            errors.push(
              maxFileSizeErrorMessage || `${file.name} is too large (max ${formatFileSize(
                maxFileSize
              )})`
            );
            return;
          }
          if (acceptedFileTypes) {
            const allowedTypes = acceptedFileTypes.split(",").map((type) => type.trim());
            const fileExtension = "." + ((_a = file.name.split(".").pop()) == null ? void 0 : _a.toLowerCase());
            const fileType = file.type;
            const isAllowed = allowedTypes.some((type) => {
              if (type.startsWith("."))
                return fileExtension === type.toLowerCase();
              return fileType === type;
            });
            if (!isAllowed) {
              errors.push(
                acceptedFileTypesErrorMessage || `${file.name} is not an allowed file type`
              );
              return;
            }
          }
          newFiles.push({
            name: file.name,
            file,
            url: URL.createObjectURL(file)
          });
        });
        if (errors.length > 0) {
          setError(errors.join(", "));
          return;
        }
        setFiles(newFiles);
        setError("");
      },
      [
        multiple,
        files,
        maxFiles,
        setFiles,
        maxFileCountErrorMessage,
        maxFileSize,
        acceptedFileTypes,
        maxFileSizeErrorMessage,
        acceptedFileTypesErrorMessage
      ]
    );
    const handleInputChange = React.useCallback(
      (e) => {
        handleFileSelect(e.target.files);
        e.target.value = "";
      },
      [handleFileSelect]
    );
    const removeFile = (index) => {
      const newFiles = files.filter((_, i) => i !== index);
      setFiles(newFiles);
    };
    const formatFileSize = (bytes) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };
    const getFileIcon = (file) => {
      const isPdf = file.name.endsWith(".pdf");
      if (isPdf) return /* @__PURE__ */ jsx(Icon, { name: "PDF", className: "size-5 text-red-500" });
      return /* @__PURE__ */ jsx(Icon, { name: "Image", className: "size-5 text-red-500" });
    };
    const hasSelectedFile = files.length > 0;
    const firstFile = files[0];
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("div", { className: root({ className }), style: { position: "relative" }, children: [
        !multiple && hasSelectedFile && /* @__PURE__ */ jsxs("div", { className: "absolute inset-0 start-1 top-5 z-20 flex items-center gap-2 bg-transparent px-4 py-2", children: [
          getFileIcon(firstFile),
          /* @__PURE__ */ jsx("span", { className: "text-s3 font-bold truncate flex-1", children: firstFile.name }),
          /* @__PURE__ */ jsx(
            FileActionButtons,
            {
              file: firstFile,
              onRemove: () => removeFile(0),
              isLoading: !!isLoading,
              className: "-translate-y-2"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "relative w-full",
            children: [
              /* @__PURE__ */ jsx(
                "input",
                {
                  ref,
                  type: "file",
                  id: id || "file-uploader",
                  className: input() + " peer",
                  multiple,
                  accept: acceptedFileTypes,
                  onChange: handleInputChange,
                  disabled: isLoading || props.disabled,
                  placeholder: " ",
                  ...props
                }
              ),
              !multiple && hasSelectedFile || /* @__PURE__ */ jsx(Slot, { className: uploadIconSlot(), children: isLoading ? /* @__PURE__ */ jsx(Icon, { name: "RefreshRotate", className: "size-6 animate-spin" }) : uploadIcon || /* @__PURE__ */ jsx(Icon, { name: "CloudUpload", className: "size-6" }) }),
              !multiple ? /* @__PURE__ */ jsx("label", { htmlFor: id || "file-uploader", className: label({}), children: placeholder }) : /* @__PURE__ */ jsx(
                "label",
                {
                  htmlFor: id || "file-uploader",
                  className: label({ className: isLoading && "animate-pulse" }),
                  children: placeholder
                }
              )
            ]
          }
        )
      ] }),
      description && /* @__PURE__ */ jsx("div", { className: descriptionSlot(), children: description }),
      showPreview && multiple && hasSelectedFile && /* @__PURE__ */ jsx("div", { className: fileList(), children: files.map((file, index) => /* @__PURE__ */ jsxs(
        "div",
        {
          className: fileItem({ className: "min-w-72" }),
          children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [
              getFileIcon(file),
              /* @__PURE__ */ jsx("span", { className: "text-s3 font-bold truncate", children: file.name })
            ] }),
            /* @__PURE__ */ jsx(
              FileActionButtons,
              {
                file,
                onRemove: () => removeFile(index),
                isLoading: !!isLoading
              }
            )
          ]
        },
        `${file.name}-${index}`
      )) }),
      (isInvalid || error || invalidMessage) && /* @__PURE__ */ jsx("div", { className: "flex mt-2 gap-1 items-center", children: /* @__PURE__ */ jsx("span", { className: "text-red-500 text-s3", children: error || invalidMessage }) })
    ] });
  }
);
const FileActionButtons = ({
  file,
  onRemove,
  isLoading,
  className = ""
}) => /* @__PURE__ */ jsxs("div", { className: className + " flex items-center gap-2", children: [
  file.url && /* @__PURE__ */ jsx(
    Button,
    {
      variant: "text",
      size: "loose",
      colorPalette: "slate",
      className: "text-slate-900",
      type: "button",
      icon: /* @__PURE__ */ jsx(
        "a",
        {
          href: file.url,
          download: file.name,
          target: "_blank",
          rel: "noopener noreferrer",
          "aria-label": `Download ${file.name}`,
          children: /* @__PURE__ */ jsx(Icon, { name: "DownloadArrow", className: "size-5" })
        }
      )
    }
  ),
  !isLoading && /* @__PURE__ */ jsx(
    Button,
    {
      variant: "text",
      size: "loose",
      colorPalette: "red",
      className: "text-slate-900",
      type: "button",
      onClick: onRemove,
      "aria-label": `Remove ${file.name}`,
      icon: /* @__PURE__ */ jsx(Icon, { name: "Trash2", className: "size-5" })
    }
  )
] });
FileUploader.displayName = "FileUploader";
export {
  FileUploader
};
